import { legacySlotRef } from '@/msgdeskRuntime/legacyBridge/legacySlotRef'
import type {
  SubRenderState,
  SubRowVariant,
  SubVisualVariant,
} from '@/msgdeskRuntime/state'
import { legacySubElementAttrs } from '@/msgdeskRuntime/legacyBridge/SelectorContracts'
import { useSubTemplateBridge } from './RowTemplateControls'
import { useRowSurface } from './RowSurfaceContext'
import { blockedPresentation } from '../Blocking/blockedPresentation'
import { QuickReplyComposer, useQuickReplyTarget } from '../Shared/QuickReplyModal'
import { useSharedSurfaceOptional } from '../Shared/SharedSurfaceContext'
import type { RowSkin } from './RowTemplateTypes'
import { resolveRegisteredRowVariant } from './RowVariantRegistry'
import { FolderRowTemplate } from './FolderRow'

function isSubmsgesSection(sub: SubRenderState) {
  return sub.legacy.data?.is_submsges_section === true
}

function subClassNames(sub: SubRenderState, variant: SubVisualVariant, skin: RowSkin) {
  const classes = ['md-sub-row', `md-sub-row--${variant}`, skin.row.base]

  if (isSubmsgesSection(sub)) {
    classes.push('md-sub-row--submsges-section')
    classes.push(sub.flags.isChecked ? 'md-sub-row--checked' : 'md-sub-row--unchecked')
  } else if (variant === 'comments') {
    classes.push('md-sub-row--answer')
  } else if (variant === 'compact') {
    classes.push('md-sub-row--answer-compact')
  } else {
    classes.push('md-sub-row--answer-std')
  }

  if (!sub.flags.isVisible) classes.push('md-sub-row--hidden', skin.row.hidden)
  if (sub.flags.isChecked) classes.push(skin.row.checked)
  if (sub.legacy.data?.is_foreign) classes.push('md-sub-row--foreign', skin.row.foreign)
  return classes.join(' ')
}

export function RowTemplateHost({
  sub,
  variant = 'auto',
  sourceMode,
}: {
  sub: SubRenderState
  variant?: SubRowVariant
  sourceMode?: string
}) {
  const surface = useRowSurface()
  const section = isSubmsgesSection(sub) ? 'submsges' : 'answers'
  const registered = variant === 'folder'
    ? null
    : resolveRegisteredRowVariant(surface.variants, section, variant, sub.viewType, sub.variant)
  const resolved = registered?.variant ?? 'folder'
  const Template = registered?.Template
  // Children keep their own legacy-selected look, so mixed trees stay mixed.
  const childVariant = surface.config.nestedTemplateVariant()
  const bridge = useSubTemplateBridge(sub, childVariant, sourceMode)
  // BLOCKED-PRESENTATION: the host keeps the normal registered variant.
  // useSubTemplateBridge replaces only the text preview and suppresses child
  // navigation/reply controls while the row is blocked.
  const presentation = blockedPresentation({
    contentLocked: bridge.display.access.isContentLocked,
    timeLocked: bridge.display.access.isTimeLocked,
    timeSide: sub.accessGrant?.time_side,
  })
  // QR-IN-PLACE: when this row's quick reply is open, the composer mounts
  // inline under the row (modern comment-box placement; the main-msg elId 0
  // composer stays with QuickReplyModal). Never on a blocked row.
  const qrTarget = useQuickReplyTarget()
  const shared = useSharedSurfaceOptional()
  const inlineQr = shared && qrTarget && qrTarget.elId === sub.elId && presentation.blocked === 'none'
    ? qrTarget
    : null

  return (
    // SKELETON: row hook virtualizes mcss-li_id-cssm_<id> for legacy selectors.
    // Visual row templates can change freely; keep this hook on the row root.
    <li
      {...legacySubElementAttrs('row', sub.elId)}
      data-md-slot={sub.legacy.rootSlot}
      ref={sub.legacy.rootSlot ? legacySlotRef(sub.legacy.rootSlot) : undefined}
      className={`${subClassNames(sub, resolved, surface.skin)}${presentation.blocked !== 'none' ? ` md-sub-row--blocked ${surface.skin.row.blocked ?? ''}` : ''}`}
      style={sub.flags.isVisible ? undefined : { display: 'none' }}
    >
      {/* FOREIGN-FROM: universal "from: sender" line for foreign rows — the
          host owns it (like checked-state and blocking) so EVERY variant
          shows the creator without per-variant work. Data is the REAL_PARENT
          facade field; absent (favs pages, anonymous rows) renders nothing. */}
      {bridge.display.identity.from ? (
        <div className={`md-sub-from ${surface.skin.row.foreignFrom ?? ''}`}>
          {'from: '}
          {sourceMode !== 'trash' && bridge.display.identity.from.href ? (
            <a href={bridge.display.identity.from.href}>{bridge.display.identity.from.name}</a>
          ) : (
            bridge.display.identity.from.name
          )}
        </div>
      ) : null}
      {Template
        ? <Template sub={sub} childVariant={childVariant} {...bridge} />
        : <FolderRowTemplate sub={sub} childVariant={childVariant} {...bridge} />}
      {inlineQr ? (
        <div className={`md-quick-reply-inline ${shared?.skin.qrInline ?? ''}`}>
          <QuickReplyComposer dialog={inlineQr} />
        </div>
      ) : null}
    </li>
  )
}
